<template>
  <v-container fluid>
    <v-overlay :model-value="loadingExport" class="align-center justify-center">
      <div class="d-flex flex-column align-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="text-h6 mt-4">Export PDF en cours...</div>
      </div>
    </v-overlay>

    <v-card>
      <v-card-title class="text-h5 pa-4">
        <v-btn
          :icon="mdiArrowLeft"
          variant="text"
          @click="goBack"
          class="mr-2"
        ></v-btn>
        {{ $t("projections.comparisonTitle") }}
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          closable
          class="mb-4"
          @click:close="clearError"
        >
          {{ error }}
        </v-alert>

        <!-- Informations client -->
        <v-card variant="outlined" class="mb-4">
          <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
            {{ $t("projections.customerInfo") }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.customerName") }}
                </div>
                <div class="text-h6">{{ selectedCustomer?.name }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.customerNumber") }}
                </div>
                <div class="text-h6">{{ selectedCustomer?.number }}</div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption text-grey">
                  {{ $t("projections.currentStatusNumber") }}
                </div>
                <v-chip
                  :color="getStatusColor(baseStats?.currentStatus)"
                  class="mt-1"
                >
                  {{ getStatusLabel(baseStats?.currentStatus) }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Boutons d'action -->
        <v-row class="mb-4">
          <v-col>
            <v-btn
              color="#ffb500"
              variant="elevated"
              :prepend-icon="mdiPlus"
              @click="showAddScenarioDialog = true"
              :disabled="scenarios.length >= 4"
            >
              {{ $t("projections.addScenario") }}
              <span class="ml-1">({{ scenarios.length }}/4)</span>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Grille de comparaison -->
        <v-row justify="center">
          <!-- Statistiques actuelles (référence) -->
          <v-col cols="12" md="4">
            <v-card variant="outlined" class="h-100">
              <v-card-title class="bg-grey-lighten-4">
                <v-icon :icon="mdiChartBar" class="mr-2"></v-icon>
                {{ $t("projections.currentStats") }}
                <v-chip size="small" class="ml-2">{{ currentYear }}</v-chip>
              </v-card-title>
              <v-card-text>
                <v-list>
                  <v-list-item
                    class="mb-2 rounded"
                    style="background-color: #fff5e6"
                  >
                    <v-list-item-title class="text-h6">
                      {{ $t("projections.totalSales") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-h5 mt-2">
                      {{ formatCurrency(baseStats?.currentYearSales) }}
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <v-chip color="#ffb500">
                        <span style="color: black; font-weight: 600">
                          {{ formatNumber(baseStats?.currentTotalPoints) }}
                          pts
                        </span>
                      </v-chip>
                    </template>
                  </v-list-item>

                  <v-divider class="my-3"></v-divider>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">{{
                          $t("projections.capitalSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{ formatCurrency(baseStats?.projectedCapitalSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(baseStats?.currentCapitalPoints) }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">
                          {{ $t("projections.implantSales") }}
                        </span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{ formatCurrency(baseStats?.projectedImplantSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(baseStats?.currentImplantPoints) }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">
                          {{ $t("projections.serviceSales") }}
                        </span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{ formatCurrency(baseStats?.projectedServiceSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(baseStats?.currentServicePoints) }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">
                          {{ $t("projections.consumableSales") }}
                        </span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{
                            formatCurrency(baseStats?.projectedConsommableSales)
                          }}
                        </span>
                        <v-chip size="small">
                          {{
                            formatNumber(baseStats?.currentConsommablePoints)
                          }}
                          pts
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Scénarios de projection (max 2 par ligne) -->
          <v-col
            v-for="scenario in scenarios"
            :key="scenario.id"
            cols="12"
            md="4"
          >
            <v-card
              variant="outlined"
              class="h-100"
              style="border: 2px solid #ffb500 !important"
            >
              <v-card-title
                class="d-flex align-center"
                style="background-color: #ffb500; color: white"
              >
                <v-icon
                  :icon="mdiChartLineVariant"
                  class="mr-2"
                  color="white"
                ></v-icon>
                <span>{{ scenario.name }}</span>
                <v-chip
                  size="small"
                  class="ml-2"
                  color="white"
                  style="color: #ffb500"
                >
                  {{ scenario.projectionYear }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn
                  :icon="mdiClose"
                  variant="text"
                  size="small"
                  color="white"
                  @click="removeScenario(scenario.id)"
                ></v-btn>
              </v-card-title>
              <v-card-text>
                <v-list>
                  <v-list-item
                    class="mb-2 rounded"
                    style="background-color: #e8f5e9"
                  >
                    <v-list-item-title class="text-h6">
                      {{ $t("projections.totalSales") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-h5 mt-2">
                      {{ formatCurrency(scenario.projectedYearSales) }}
                      <v-chip
                        :color="
                          getEvolutionColor(
                            getEvolution(
                              baseStats?.currentYearSales,
                              scenario.projectedYearSales,
                            ),
                          )
                        "
                        size="small"
                        class="ml-2"
                      >
                        <v-icon
                          :icon="
                            getEvolution(
                              baseStats?.currentYearSales,
                              scenario.projectedYearSales,
                            ) >= 0
                              ? mdiTrendingUp
                              : mdiTrendingDown
                          "
                          size="small"
                          class="mr-1"
                        ></v-icon>
                        {{
                          formatPercentage(
                            getEvolution(
                              baseStats?.currentYearSales,
                              scenario.projectedYearSales,
                            ),
                          )
                        }}
                      </v-chip>
                    </v-list-item-subtitle>
                    <template v-slot:append>
                      <v-chip color="#f89200">
                        <span style="color: black; font-weight: 600">
                          {{ formatNumber(scenario.projectedTotalPoints) }} pts
                        </span>
                        <v-chip
                          :color="
                            getEvolutionColor(
                              getEvolution(
                                baseStats?.currentTotalPoints,
                                scenario.projectedTotalPoints,
                              ),
                            )
                          "
                          size="x-small"
                          class="ml-1"
                        >
                          {{
                            formatPercentage(
                              getEvolution(
                                baseStats?.currentTotalPoints,
                                scenario.projectedTotalPoints,
                              ),
                            )
                          }}
                        </v-chip>
                      </v-chip>
                    </template>
                  </v-list-item>

                  <v-divider class="my-3"></v-divider>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">{{
                          $t("projections.capitalSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{ formatCurrency(scenario.projectedCapitalSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(scenario.projectedCapitalPoints) }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentCapitalPoints,
                                  scenario.projectedCapitalPoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentCapitalPoints,
                                  scenario.projectedCapitalPoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">{{
                          $t("projections.implantSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{ formatCurrency(scenario.projectedImplantSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(scenario.projectedImplantPoints) }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentImplantPoints,
                                  scenario.projectedImplantPoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentImplantPoints,
                                  scenario.projectedImplantPoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">{{
                          $t("projections.serviceSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{ formatCurrency(scenario.projectedServiceSales) }}
                        </span>
                        <v-chip size="small">
                          {{ formatNumber(scenario.projectedServicePoints) }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentServicePoints,
                                  scenario.projectedServicePoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentServicePoints,
                                  scenario.projectedServicePoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-list-item dense>
                    <v-row no-gutters align="center">
                      <v-col cols="3">
                        <span class="text-caption">{{
                          $t("projections.consumableSales")
                        }}</span>
                      </v-col>
                      <v-col
                        cols="9"
                        class="d-flex justify-space-around align-center"
                      >
                        <span class="text-caption font-weight-medium">
                          {{
                            formatCurrency(scenario.projectedConsommableSales)
                          }}
                        </span>
                        <v-chip size="small">
                          {{
                            formatNumber(scenario.projectedConsommablePoints)
                          }}
                          pts
                          <span
                            :class="
                              getEvolutionClass(
                                getEvolution(
                                  baseStats?.currentConsommablePoints,
                                  scenario.projectedConsommablePoints,
                                ),
                              )
                            "
                          >
                            ({{
                              formatPercentage(
                                getEvolution(
                                  baseStats?.currentConsommablePoints,
                                  scenario.projectedConsommablePoints,
                                ),
                              )
                            }})
                          </span>
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-list-item>

                  <v-divider class="my-3"></v-divider>

                  <!-- Statut projeté -->
                  <v-list-item dense>
                    <v-list-item-title>{{
                      $t("projections.projectedStatus")
                    }}</v-list-item-title>
                    <template v-slot:append>
                      <v-chip
                        :color="
                          getStatusColor(
                            scenario.statusOverrideUsed
                              ? scenario.statusOverrideUsed
                              : scenario.projectedStatus,
                          )
                        "
                        size="small"
                      >
                        {{
                          getStatusLabel(
                            scenario.statusOverrideUsed
                              ? scenario.statusOverrideUsed
                              : scenario.projectedStatus,
                          )
                        }}
                      </v-chip>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn variant="outlined" @click="goBack" :prepend-icon="mdiArrowLeft">
          {{ $t("projections.backToList") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="#f89200"
          variant="elevated"
          :prepend-icon="mdiFileExport"
          @click="exportToPdf"
          :disabled="scenarios.length === 0 || scenarios.length > 5"
          :loading="loading"
          class="text-white"
        >
          {{ $t("projections.export") }}
          <v-tooltip
            v-if="scenarios.length === 0"
            activator="parent"
            location="top"
          >
            Vous devez avoir au moins 1 scénario pour exporter
          </v-tooltip>
          <v-tooltip
            v-else-if="scenarios.length > 5"
            activator="parent"
            location="top"
          >
            Maximum 5 scénarios pour l'export
          </v-tooltip>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog pour ajouter un scénario -->
    <v-dialog v-model="showAddScenarioDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 pa-4">
          {{ $t("projections.addScenario") }}
        </v-card-title>
        <v-card-text>
          <v-form ref="scenarioForm">
            <v-text-field
              v-model="newScenario.name"
              :label="$t('projections.scenarioName')"
              variant="outlined"
              :rules="[(v) => !!v || $t('validations.required')]"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model.number="newScenario.capitalProjection"
              :label="$t('projections.capitalProjection')"
              type="number"
              variant="outlined"
              :hint="$t('projections.capitalHint')"
              prefix="€"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model.number="newScenario.implantProjection"
              :label="$t('projections.implantProjection')"
              type="number"
              variant="outlined"
              :hint="$t('projections.implantHint')"
              prefix="€"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model.number="newScenario.serviceProjection"
              :label="$t('projections.serviceProjection')"
              type="number"
              variant="outlined"
              :hint="$t('projections.serviceHint')"
              prefix="€"
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model.number="newScenario.consumableProjection"
              :label="$t('projections.consumableProjection')"
              type="number"
              variant="outlined"
              :hint="$t('projections.consumableHint')"
              prefix="€"
              class="mb-3"
            ></v-text-field>

            <v-alert type="info" variant="tonal" class="mb-3">
              {{ $t("projections.totalProjectedSales") }}:
              {{ formatCurrency(totalNewScenarioSales) }}
            </v-alert>

            <v-select
              v-model="newScenario.overrideStatusId"
              :items="statusOptions"
              item-title="label"
              item-value="value"
              label="Forcer un statut spécifique (optionnel)"
              variant="outlined"
              density="comfortable"
              clearable
              hint="Laissez vide pour laisser le calcul automatique déterminer le statut"
              persistent-hint
              class="mb-3"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-chip
                      :color="getStatusColor(item.raw.value)"
                      size="x-small"
                      class="mr-2"
                    ></v-chip>
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip :color="getStatusColor(item.raw.value)" size="small">
                  {{ item.raw.label }}
                </v-chip>
              </template>
            </v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showAddScenarioDialog = false">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn
            color="#ffb500"
            variant="elevated"
            @click="addScenario"
            :loading="loading"
          >
            {{ $t("projections.calculate") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import {
  mdiArrowLeft,
  mdiChartBar,
  mdiChartLineVariant,
  mdiTrendingUp,
  mdiTrendingDown,
  mdiPlus,
  mdiClose,
  mdiFileExport,
} from "@mdi/js";
import projections from "../api/projections";
import { getStatusColor } from "@/utils/statusColors";

export default {
  name: "ProjectionComparison",
  data() {
    return {
      mdiArrowLeft,
      mdiChartBar,
      mdiChartLineVariant,
      mdiTrendingUp,
      mdiTrendingDown,
      mdiPlus,
      mdiClose,
      mdiFileExport,
      showAddScenarioDialog: false,
      baseStats: null, // Statistiques actuelles (référence)
      scenarios: [],
      statusOptions: [
        { value: 0, label: "Hors programme" },
        { value: 1, label: "Statut 1" },
        { value: 2, label: "Statut 2" },
        { value: 3, label: "Statut 3" },
        { value: 4, label: "Statut 4" },
      ],
      newScenario: {
        name: "",
        capitalProjection: 0,
        implantProjection: 0,
        serviceProjection: 0,
        consumableProjection: 0,
        overrideStatusId: null,
      },
    };
  },
  computed: {
    ...mapState("projections", [
      "selectedCustomer",
      "projectionForm",
      "projectionResults",
      "error",
      "currentYear",
      "currentYearStats",
      "loading",
      "loadingExport",
    ]),
    totalNewScenarioSales() {
      return (
        (this.newScenario.capitalProjection || 0) +
        (this.newScenario.implantProjection || 0) +
        (this.newScenario.serviceProjection || 0) +
        (this.newScenario.consumableProjection || 0)
      );
    },
  },
  async mounted() {
    // Utiliser les statistiques déjà calculées dans ProjectionForm (N-1 avec statut par défaut)
    // Évite un appel API inutile puisque ces stats ne changent pas
    if (this.currentYearStats) {
      this.baseStats = this.currentYearStats;
    } else if (this.selectedCustomer) {
      // Fallback : charger uniquement si les stats n'existent pas encore
      await this.loadBaseStats();
    }

    // Si une projection existe déjà, l'ajouter comme premier scénario
    if (this.projectionResults && this.scenarios.length === 0) {
      this.scenarios.push({
        ...this.projectionResults,
        name: this.$t("projections.scenario") + " 1",
        id: Date.now(),
      });
    }
  },
  beforeUnmount() {
    // Nettoyer les données locales lors de la destruction du composant
    this.scenarios = [];
    this.baseStats = null;
  },
  methods: {
    async loadBaseStats() {
      try {
        this.$store.commit("projections/setLoading", true);
        // Important : Ne jamais inclure overrideStatusId pour les stats de base N-1
        // Ces statistiques doivent toujours être calculées avec le statut par défaut du client
        const payload = {
          customerId: this.selectedCustomer.id,
          projectionYear: this.currentYear,
          capitalInput: { inputType: "EurosAmount", value: 0 },
          implantInput: { inputType: "EurosAmount", value: 0 },
          serviceInput: { inputType: "EurosAmount", value: 0 },
          consommableInput: { inputType: "EurosAmount", value: 0 },
          // NE PAS inclure overrideStatusId ici
        };
        const response = await projections.calculate(payload);
        this.baseStats = response.data;
      } catch {
        this.$store.dispatch("showSnackbar", {
          message: this.$t("alert.error.generic"),
          color: "error",
        });
      } finally {
        this.$store.commit("projections/setLoading", false);
      }
    },
    async exportToPdf() {
      if (this.scenarios.length === 0 || this.scenarios.length > 5) {
        return;
      }

      try {
        const exportData = {
          baseStatistics: this.baseStats,
          scenarios: this.scenarios.map((scenario) => ({
            scenarioName: scenario.name,
            projection: scenario,
          })),
        };

        const response = await this.$store.dispatch(
          "projections/exportProjectionsToPdf",
          exportData,
        );

        // Créer un blob et télécharger le PDF
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download =
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") ||
          `Projections_${this.selectedCustomer?.name}_${this.currentYear}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch {
        this.$store.dispatch("showSnackbar", {
          message: this.$t("alert.error.generic"),
          color: "error",
        });
      }
    },
    async addScenario() {
      const { valid } = await this.$refs.scenarioForm.validate();
      if (!valid) return;

      try {
        this.$store.commit("projections/setLoading", true);
        const payload = {
          customerId: this.selectedCustomer.id,
          projectionYear: this.currentYear,
          capitalInput: {
            inputType: "EurosAmount",
            value: this.newScenario.capitalProjection || 0,
          },
          implantInput: {
            inputType: "EurosAmount",
            value: this.newScenario.implantProjection || 0,
          },
          serviceInput: {
            inputType: "EurosAmount",
            value: this.newScenario.serviceProjection || 0,
          },
          consommableInput: {
            inputType: "EurosAmount",
            value: this.newScenario.consumableProjection || 0,
          },
        };

        // Ajouter overrideStatusId si défini
        if (
          this.newScenario.overrideStatusId !== null &&
          this.newScenario.overrideStatusId !== undefined
        ) {
          payload.overrideStatusId = this.newScenario.overrideStatusId;
        }

        const response = await projections.calculate(payload);

        // Ajouter le scénario avec le nom personnalisé
        this.scenarios.push({
          ...response.data,
          name: this.newScenario.name,
          id: Date.now(),
        });

        // Réinitialiser le formulaire
        this.newScenario = {
          name: "",
          capitalProjection: 0,
          implantProjection: 0,
          serviceProjection: 0,
          consumableProjection: 0,
          overrideStatusId: null,
        };
        this.showAddScenarioDialog = false;
        this.$refs.scenarioForm.reset();
      } catch {
        this.$store.commit(
          "projections/setError",
          "Erreur lors du calcul de la projection",
        );
      } finally {
        this.$store.commit("projections/setLoading", false);
      }
    },
    removeScenario(scenarioId) {
      this.scenarios = this.scenarios.filter((s) => s.id !== scenarioId);
    },
    goBack() {
      this.$emit("go-back");
    },
    clearError() {
      this.$store.commit("projections/setError", null);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(value || 0);
    },
    formatNumber(value) {
      return new Intl.NumberFormat("fr-FR").format(Math.round(value || 0));
    },
    formatPercentage(value) {
      if (!value && value !== 0) return "N/A";
      const sign = value >= 0 ? "+" : "";
      return `${sign}${value.toFixed(1)}%`;
    },
    /**
     * Calcule l'évolution en pourcentage entre deux valeurs
     * Gère correctement les cas où la valeur de départ est négative
     */
    getEvolution(current, projected) {
      if (!current || current === 0) return 0;

      // Cas spécial : si on part d'une valeur négative
      if (current < 0) {
        // Si on passe de négatif à positif ou zéro : croissance positive
        if (projected >= 0) {
          // Calcul basé sur la valeur absolue pour avoir un pourcentage positif
          return (Math.abs(projected - current) / Math.abs(current)) * 100;
        }
        // Si on reste négatif : on inverse le calcul
        // Si projected > current (ex: -50 > -159), c'est une amélioration (positif)
        // Si projected < current (ex: -200 < -159), c'est une dégradation (négatif)
        return ((projected - current) / Math.abs(current)) * 100;
      }

      // Cas normal : valeur de départ positive
      return ((projected - current) / current) * 100;
    },
    getEvolutionColor(evolution) {
      if (evolution > 0) return "success";
      if (evolution < 0) return "error";
      return "grey";
    },
    getEvolutionClass(evolution) {
      if (evolution > 0) return "text-success";
      if (evolution < 0) return "text-error";
      return "text-grey";
    },
    getStatusColor,
    getStatusLabel(status) {
      if (status === -1 || status === 0 || status === "-1" || status === "0") {
        return "Hors programme";
      }
      return status !== undefined && status !== null ? status.toString() : "0";
    },
  },
};
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}

.text-success {
  color: #4caf50 !important;
  font-weight: 600;
}

.text-error {
  color: #f44336 !important;
  font-weight: 600;
}

.text-grey {
  color: #9e9e9e !important;
}

.h-100 {
  height: 100%;
}
</style>
